import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime, map } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { AuthService } from '../../../../../core/services/auth.service';
import { LanguageService } from '../../../../../core/services/language.service';
import { ButtonComponent } from '../../../../../osp-ui/components/button/button/button.component';
import { CardOptionComponent } from '../../../../../osp-ui/components/form/card-option-list/components/card-option/card-option.component';
import { DropdownComponent } from '../../../../../osp-ui/components/form/dropdown/dropdown.component';
import { ErrorComponent } from '../../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../../osp-ui/components/form/form-field/form-field.component';
import { InputDateComponent } from '../../../../../osp-ui/components/form/input-date/input-date.component';
import { InputTextComponent } from '../../../../../osp-ui/components/form/input-text/input-text.component';
import { InputTextareaComponent } from '../../../../../osp-ui/components/form/input-textarea/input-textarea.component';
import { StepComponent } from '../../../../../osp-ui/components/panel/stepper/components/step/step.component';
import { StepContentDirective } from '../../../../../osp-ui/components/panel/stepper/directives/step-content.directive';
import { StepFooterDirective } from '../../../../../osp-ui/components/panel/stepper/directives/step-footer.directive';
import { StepperComponent } from '../../../../../osp-ui/components/panel/stepper/stepper.component';
import { MapSearchPlaceResult } from '../../../../../shared/components/maps/contract/map-search-place-result.contract';
import { MapLoaderService } from '../../../../../shared/components/maps/services/map-loader.service';
import { Address } from '../../../../../shared/contract/address.contract';
import { createDialogResult, DIALOG_ACTION, DialogResult } from '../../../../../shared/contract/dialog-result.contract';
import { Authorities } from '../../../../../shared/enums/authorities.enum';
import { IconPipe } from '../../../../../shared/pipes/icon.pipe';
import { AddressService } from '../../../../../shared/services/address.service';
import { OptionConverterService } from '../../../../../shared/services/option-converter.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { EquipmentsApiService } from '../../../../resources/equipment/services/equipments-api.service';
import { LocationAddressFormComponent } from '../../../shared/components/location-address-form/location-address-form.component';
import { ViewProject } from '../../contract/view-project.contract';
import { ProjectApiService } from '../../services/project-api.service';
import { ProjectType } from '../../enums/project-type.enum';
import { ProjectAddEditDialogFormController } from './services/project-add-edit-dialog-form-controller.service';

@Component({
  selector: 'osp-project-add-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    InputTextComponent,
    InputDateComponent,
    InputTextareaComponent,
    StepperComponent,
    StepComponent,
    StepContentDirective,
    StepFooterDirective,
    IconPipe,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
    DropdownComponent,
    CardOptionComponent,
    NgTemplateOutlet,
    LocationAddressFormComponent,
  ],
  providers: [ProjectAddEditDialogFormController],
  templateUrl: './project-add-edit-dialog.component.html',
  styleUrls: ['./project-add-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectAddEditDialogComponent {
  readonly formService = inject(ProjectAddEditDialogFormController);
  private readonly dialogRef = inject(DynamicDialogRef<DialogResult<string>>);
  private readonly dialogConfig = inject<DynamicDialogConfig<{ project?: ViewProject }>>(DynamicDialogConfig);
  private readonly projectApiService = inject(ProjectApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  private readonly mapLoaderService = inject(MapLoaderService);
  private readonly addressService = inject(AddressService);
  private readonly optionConverterService = inject(OptionConverterService);
  private readonly equipmentsApiService = inject(EquipmentsApiService);
  private readonly toastService = inject(ToastService);

  private readonly ORGANISATIONS_PAGE_SIZE = 5000;

  protected readonly isEditMode = !!this.dialogConfig.data?.project;
  protected readonly IconKey = IconKey;

  readonly form = this.formService.buildForm(this.dialogConfig.data?.project);

  readonly $organisations = toSignal(
    this.equipmentsApiService
      .getOrganisations(0, this.ORGANISATIONS_PAGE_SIZE)
      .pipe(map((res) => this.optionConverterService.convertOrganisationInfo(res.content))),
    { initialValue: [] },
  );
  readonly isMapLoaded$ = this.mapLoaderService.isLoaded;

  mapCenter: google.maps.LatLngLiteral = { lat: 51.1657, lng: 10.4515 };
  mapMarkerPosition: google.maps.LatLngLiteral | null = null;
  private isAddressGeocodePending = false;
  private readonly $projectType = signal<ProjectType | null | undefined>(
    this.form.controls.projectData.controls.projectType.value,
  );

  readonly $selectedOrganisation = computed(() => {
    const orgId = this.form.controls.projectData.controls.organisationId.value;
    return this.$organisations().find((o) => o.value === orgId)?.label || '-';
  });

  readonly projectTypeOptions = [
    { type: ProjectType.FIX, label: this.languageService.getInstant('modules.locations.category.project.fix') },
    { type: ProjectType.MOBIL, label: this.languageService.getInstant('modules.locations.category.project.mobil') },
  ];

  readonly locationInfoLabel = this.languageService.getInstant('modules.locations.project.create.infoBox.title');
  readonly locationInfoDescription = this.languageService.getInstant(
    'shared.createEntity.entitySelection.project.description',
  );

  readonly showAutomaticGeofence = !this.isEditMode && this.authService.hasAuthority(Authorities.GEOFENCE_MANAGE);

  constructor() {
    const lat = this.form.controls.projectAddress.controls.lat.value;
    const lon = this.form.controls.projectAddress.controls.lon.value;
    if (this.hasValidLocation(lat, lon)) {
      this.setMapLocation(lat!, lon!);
    }

    this.form.controls.projectAddress.valueChanges
      .pipe(debounceTime(environment.DELAY_SHORTEST), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const address = this.addressService.format(value);
        if (!address.length || !this.isGoogleMapsLoaded()) {
          this.isAddressGeocodePending = false;
          this.updateAddressResolvedError(false);
          return;
        }

        this.isAddressGeocodePending = true;
        new google.maps.Geocoder().geocode({ address }, (results, status) => {
          this.isAddressGeocodePending = false;
          if (status !== 'OK' || !results?.length) {
            this.updateAddressResolvedError(true);
            return;
          }

          const location = results[0].geometry?.location;
          if (!location) return;

          const latResult = location.lat();
          const lonResult = location.lng();
          this.form.controls.projectAddress.patchValue({ lat: latResult, lon: lonResult }, { emitEvent: false });
          this.updateAddressResolvedError(false);
          this.setMapLocation(latResult, lonResult);
        });
      });

    this.updateAddressStepState(this.form.controls.projectData.controls.projectType.value);
    this.form.controls.projectData.controls.projectType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        this.$projectType.set(type);
        this.updateAddressStepState(type);
      });
  }

  handleMapClick(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;

    const lat = event.latLng.lat();
    const lon = event.latLng.lng();
    this.updateAddressFromCoordinates(lat, lon);
  }

  handleSearchLocationChange({ lat, lon, street, streetNumber, postalCode, city }: MapSearchPlaceResult): void {
    if (this.hasAnyAddressPart({ street, streetNumber, postalCode, city })) {
      this.form.controls.projectAddress.patchValue(
        {
          city,
          lat,
          lon,
          postalCode,
          street,
          streetNumber,
        },
        { emitEvent: false },
      );
      this.setMapLocation(lat, lon);
      this.updateAddressResolvedError(false);
      return;
    }

    this.updateAddressFromCoordinates(lat, lon);
  }

  private updateAddressFromCoordinates(lat: number, lon: number): void {
    this.form.controls.projectAddress.patchValue({ lat, lon }, { emitEvent: false });
    this.setMapLocation(lat, lon);

    if (!this.isGoogleMapsLoaded()) return;

    new google.maps.Geocoder().geocode({ location: { lat, lng: lon } }, (results, status) => {
      if (status !== 'OK' || !results?.length) {
        this.updateAddressResolvedError(true);
        return;
      }

      const address = this.extractAddress(results[0]);
      this.form.controls.projectAddress.patchValue(
        {
          street: address.street,
          streetNumber: address.streetNumber,
          postalCode: address.postalCode,
          city: address.city,
          lat,
          lon,
        },
        { emitEvent: false },
      );
      this.updateAddressResolvedError(false);
    });
  }

  onCancel(): void {
    this.dialogRef.close(createDialogResult(DIALOG_ACTION.GO_BACK));
  }

  handleSubmit(): void {
    if (this.form.invalid || this.form.pending || this.isAddressGeocodePending) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEditMode) {
      this.handleUpdate();
    } else {
      this.handleCreate();
    }
  }

  private handleCreate(): void {
    const command = this.formService.getCreateCommand();
    this.projectApiService
      .createProject(command)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toastService.showSuccess({
          summary: this.languageService.getInstant('modules.locations.project.create.toast.success'),
        });
        this.dialogRef.close(createDialogResult(DIALOG_ACTION.SAVE));
        this.router.navigate(['/locations/active']);
      });
  }

  private handleUpdate(): void {
    const command = this.formService.getUpdateCommand();
    this.projectApiService
      .updateProject(command)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toastService.showSuccess({
          summary: this.languageService.getInstant('modules.locations.project.edit.toast.success'),
        });
        this.dialogRef.close(createDialogResult(DIALOG_ACTION.SAVE));
        this.router.navigate(['/locations/active']);
      });
  }

  private setMapLocation(lat: number, lng: number): void {
    this.mapCenter = { lat, lng };
    this.mapMarkerPosition = { lat, lng };
  }

  private hasValidLocation(lat?: number | null, lng?: number | null): boolean {
    return typeof lat === 'number' && Number.isFinite(lat) && typeof lng === 'number' && Number.isFinite(lng);
  }

  private hasAnyAddressPart(address: Partial<Address>): boolean {
    return Object.values(address).some((value) => `${value ?? ''}`.trim().length > 0);
  }

  private isGoogleMapsLoaded(): boolean {
    return typeof google !== 'undefined' && !!google.maps;
  }

  protected get isSubmitDisabled(): boolean {
    return this.form.invalid || this.form.pending || this.isAddressGeocodePending;
  }

  private updateAddressStepState(type: ProjectType | null | undefined): void {
    if (type) {
      this.form.controls.projectAddress.controls.lat.addValidators(Validators.required);
      this.form.controls.projectAddress.controls.lon.addValidators(Validators.required);
      this.form.controls.projectAddress.controls.lat.updateValueAndValidity({ emitEvent: false });
      this.form.controls.projectAddress.controls.lon.updateValueAndValidity({ emitEvent: false });
      this.form.controls.projectAddress.enable({ emitEvent: false });
      return;
    }

    this.form.controls.projectAddress.disable({ emitEvent: false });
    this.form.controls.projectAddress.controls.lat.removeValidators(Validators.required);
    this.form.controls.projectAddress.controls.lon.removeValidators(Validators.required);
    this.form.controls.projectAddress.controls.lat.updateValueAndValidity({ emitEvent: false });
    this.form.controls.projectAddress.controls.lon.updateValueAndValidity({ emitEvent: false });
    this.updateAddressResolvedError(false);
  }

  private updateAddressResolvedError(isNotResolved: boolean): void {
    const currentErrors = this.form.controls.projectAddress.errors || {};
    if (isNotResolved) {
      this.form.controls.projectAddress.setErrors({ ...currentErrors, notResolved: true });
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { notResolved, ...restErrors } = currentErrors;
    this.form.controls.projectAddress.setErrors(Object.keys(restErrors).length ? restErrors : null);
  }

  private extractAddress(result: google.maps.GeocoderResult): Address {
    return {
      street: this.getAddressPart(result, 'route'),
      streetNumber: this.getAddressPart(result, 'street_number'),
      postalCode: this.getAddressPart(result, 'postal_code'),
      city:
        this.getAddressPart(result, 'locality') ||
        this.getAddressPart(result, 'postal_town') ||
        this.getAddressPart(result, 'administrative_area_level_2'),
    };
  }

  private getAddressPart(result: google.maps.GeocoderResult, type: string, useShortName = false): string {
    const part = result.address_components.find((component) => component.types.includes(type));
    return (useShortName ? part?.short_name : part?.long_name) || '';
  }
}
